import React, { useRef, useState } from 'react';

import { MFA_APP_KEY, MFA_SMS_KEY, MFA_EMAIL_KEY } from 'lib/mfa';
import Steps from '../ModalController/Steps';
import SelectAuthenticator from './SelectAuthenticator';
import SetupApp from './SetupApp';
import SetupSms from './SetupSms';
import SetupEmail from './SetupEmail';
import ValidateAuthenticator from './ValidateAuthenticator';

import './style.scss';
import Portal from 'components/Portal';
import { ButtonV2 } from 'components';

function AddVerificationMethod({ data: { authenticators, userEmail }, onClose, onCancelClose, isConfirmingClose }) {
  const stepsRef = useRef(null);

  const [selectedAuthenticator, setSelectedAuthenticator] = useState(authenticators.find(a => !a.enabled));
  const [isConfirmingPrevious, setIsConfirmingPrevious] = useState(false);
  const [allowPrevious, setAllowPrevious] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [otpSecret, setOtpSecret] = useState(null);
  const [email, setEmail] = useState(null);

  const onPrevious = () => {
    if (selectedAuthenticator.kind === MFA_APP_KEY && stepsRef.current.state.currentStep === 1) {
      setIsConfirmingPrevious(true);
      setAllowPrevious(false);
      return;
    }

    switch (stepsRef.current.state.currentStep) {
      case 1:
        stepsRef.current.goToStep('select');
        break;
      case 2:
        stepsRef.current.goToStep('setup');
        break;
    }
  };

  const movePrevious = () => {
    setIsConfirmingPrevious(false);
    setAllowPrevious(true);
    stepsRef.current.goToStep('select');
  };

  const onContinue = context => {
    if (context?.email) {
      setEmail(context.email);
    }

    if (context?.selectedAuthenticator) {
      setSelectedAuthenticator(context.selectedAuthenticator);
    }

    if (context?.qrCode) {
      setQrCode(context.qrCode);
      setIsConfirmingPrevious(false);
      setAllowPrevious(true);
    }

    if (context?.otpSecret) {
      setOtpSecret(context.otpSecret);
    }

    switch (stepsRef.current.state.currentStep) {
      case 0:
        stepsRef.current.goToStep('setup');
        break;
      case 1:
        stepsRef.current.goToStep('validate');
        break;
      case 2:
        onClose();
        break;
    }
  };

  const getSetupComponent = authenticator => {
    switch (authenticator.kind) {
      case MFA_APP_KEY:
        return (
          <SetupApp
            onPrevious={onPrevious}
            onContinue={onContinue}
            currentQrCode={qrCode}
            currentOtpSecret={otpSecret}
          />
        );
      case MFA_SMS_KEY:
        return <SetupSms onPrevious={onPrevious} onContinue={onContinue} email={email} />;
      case MFA_EMAIL_KEY:
        return <SetupEmail onPrevious={onPrevious} onContinue={onContinue} email={userEmail} />;
      default:
        return <div>Unknown verification method selected.</div>;
    }
  };

  const renderPreviousConfirmation = () => {
    if (!isConfirmingPrevious) {
      return null;
    }

    return (
      <Portal>
        <div className="modal modal--visible mfa-add-method__close-confirmation-modal">
          <div className="modal__window">
            <div className="modal__wrapper create-program">
              <div className="modal__header">
                <span className="modal__header-title">Incomplete Setup</span>
              </div>
              <div className="modal__container">
                <div className="modal__content">
                  <div>
                    If you scanned the QR code or manually entered the code into your Authenticator App, you must
                    verify, then click Save & Finish to complete setup.
                  </div>
                  <div>
                    If setup is incomplete, you must delete the account from your Authenticator App and start the
                    process over.
                  </div>
                  <div>
                    <strong>Would you like to cancel setup?</strong>
                  </div>
                </div>
                <div className="form__row form__row--actions">
                  <ButtonV2
                    label="No, Return to Setup"
                    onClick={() => {
                      setIsConfirmingPrevious(false);
                    }}
                  />
                  <ButtonV2
                    secondary
                    label="Yes, Cancel Setup"
                    onClick={() => {
                      movePrevious();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    );
  };

  const renderCloseConfirmation = () => {
    if (!isConfirmingClose) {
      return null;
    }

    if (selectedAuthenticator.kind !== MFA_APP_KEY || stepsRef.current.state.currentStep === 0) {
      onClose(false);
    }

    return (
      <Portal>
        <div className="modal modal--visible mfa-add-method__close-confirmation-modal">
          <div className="modal__window">
            <div className="modal__wrapper create-program">
              <div className="modal__header">
                <span className="modal__header-title">Incomplete Setup</span>
              </div>
              <div className="modal__container">
                <div className="modal__content">
                  <div>
                    If you scanned the QR code or manually entered the code into your Authenticator App, you must
                    verify, then click Save & Finish to complete setup.
                  </div>
                  <div>
                    If setup is incomplete, you must delete the account from your Authenticator App and start the
                    process over.
                  </div>
                  <div>
                    <strong>Would you like to cancel setup?</strong>
                  </div>
                </div>
                <div className="form__row form__row--actions">
                  <ButtonV2 label="No, Return to Setup" onClick={() => onCancelClose()} />
                  <ButtonV2 secondary label="Yes, Cancel Setup" onClick={() => onClose(false)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    );
  };

  return (
    <div className="modal__wrapper mfa-add-method">
      <Steps ref={stepsRef}>
        <Steps.Item name="select">
          <SelectAuthenticator authenticators={authenticators} onContinue={onContinue} />
        </Steps.Item>
        <Steps.Item name="setup">{getSetupComponent(selectedAuthenticator)}</Steps.Item>
        <Steps.Item name="validate">
          <ValidateAuthenticator
            onPrevious={() => allowPrevious && onPrevious()}
            onContinue={onContinue}
            otpSecret={otpSecret}
            authenticator={selectedAuthenticator}
          />
        </Steps.Item>
      </Steps>
      {renderPreviousConfirmation()}
      {renderCloseConfirmation()}
    </div>
  );
}

export default AddVerificationMethod;
